import React, { useCallback, useState } from 'react';
import { PropertyDetails } from 'src/types/flow';
import { Accordion, Button, Col, Grid, Row, Stack, Text } from 'rsuite';
import { CustomHTML } from 'src/components/common/CustomHTML';
import { ExpirationApplicationQuestion } from 'src/types/configuration';
import { SupplementalQuestion } from 'src/types/supplementalQuestions';

interface SupplementalQuestionsProps {
  propertyDetails?: PropertyDetails;
  questions: Record<string, SupplementalQuestion>;
  configQuestions: Array<ExpirationApplicationQuestion>;
}
export const SupplementalQuestions = ({
  questions,
  configQuestions,
}: SupplementalQuestionsProps) => {
  const [showMoreState, setShowMoreState] = useState<Record<number, boolean>>(
    {}
  );
  const mapSupplementalQuestionAnswerToLabel = useCallback(
    (questionKey: string, answerKey: string): string => {
      const matchingQuestion = configQuestions.find(
        (q) => q.key === questionKey
      );
      if (!matchingQuestion) {
        return '';
      }
      if (matchingQuestion.answer_type !== 'radio') {
        return answerKey;
      }
      return (
        matchingQuestion.radio_options?.find((opt) => opt.key === answerKey)
          ?.label ?? ''
      );
    },
    [configQuestions]
  );

  return (
    <Row>
      <Col xs={24}>
        <Accordion className="mh-0 mt-16">
          <Accordion.Panel
            header={
              <h2
                style={{
                  marginLeft: '-8px',
                  borderBottom: '1px solid #DEDEDE',
                }}
              >
                Supplemental Questions
              </h2>
            }
            className="accordion-without-padding"
          >
            {configQuestions?.map((question, idx) => {
              // Find matching answer
              const result = questions?.[question.key];
              const questionText = result?.question;
              let answer = result?.answer;
              if (typeof answer === 'boolean') {
                answer = answer === true ? 'Yes' : 'No';
              }
              if (!answer) {
                return <></>;
              }
              const mappedAnswer = mapSupplementalQuestionAnswerToLabel(
                question.key,
                answer
              );
              const allowShowMore = mappedAnswer?.length > 150;
              const doShowMore = showMoreState[idx] || false;

              return (
                <Grid
                  key={`supplemental_question_${idx}`}
                  className="mb-8"
                  fluid
                >
                  <Row>
                    <Col
                      xs={24}
                      md={10}
                      lg={8}
                    >
                      <Text>
                        <CustomHTML text={questionText} />
                      </Text>
                    </Col>
                    <Col
                      xs={24}
                      md={14}
                      lg={16}
                    >
                      {allowShowMore && (
                        <Stack
                          direction="column"
                          alignItems="flex-end"
                        >
                          {doShowMore ? (
                            <div className="text-bold mb-0">{mappedAnswer}</div>
                          ) : (
                            <div className="truncated-supplemental-question-answer">
                              <div className="text-bold mb-0">
                                {mappedAnswer}
                              </div>
                            </div>
                          )}

                          <Button
                            className="p-0 m-0"
                            appearance="link"
                            onClick={() => {
                              setShowMoreState((prevState) => ({
                                ...prevState,
                                [idx]: !doShowMore,
                              }));
                            }}
                          >
                            {doShowMore ? '[Show less]' : '[Show more]'}
                          </Button>
                        </Stack>
                      )}
                      {!allowShowMore && (
                        <div className="text-bold">{mappedAnswer}</div>
                      )}
                    </Col>
                  </Row>
                </Grid>
              );
            })}
          </Accordion.Panel>
        </Accordion>
      </Col>
    </Row>
  );
};
